/**
 * Learning & Development API client
 */

import apiClient from '@/core/api/client'
import { API_ENDPOINTS } from '@/core/api/endpoints'
import type {
  TrainingCategory,
  Training,
  EmployeeTraining,
  TrainingStats,
  TrainingCertificate,
  TrainingDocument,
  CreateTrainingCategoryInput,
  UpdateTrainingCategoryInput,
  CreateTrainingInput,
  UpdateTrainingInput,
  CreateEmployeeTrainingInput,
  AdminCreateEmployeeTrainingInput,
  UpdateEmployeeTrainingInput,
  TrainingFilters,
  EmployeeTrainingFilters,
  TrainingsResponse,
  EmployeeTrainingsResponse,
  CategoriesResponse,
  SimilarTrainingsResponse,
  TrainingRequirement,
  TrainingRequirementFilters,
  TrainingRequirementsResponse,
  CreateTrainingRequirementInput,
  BulkCreateTrainingRequirementInput,
  UpdateTrainingRequirementInput,
  TrainingReportFilters,
  ComprehensiveTrainingReport,
  ReportEmployee,
  ReportDepartment,
  TrainingRecordForReport,
  Pagination,
  TrainingReview,
  TrainingReviewsResponse,
  CreateTrainingReviewInput,
  UpdateTrainingReviewInput,
  CertificationStatusReport,
} from '../types/learning.types'

export const learningApi = {
  // ==================== Categories ====================

  getCategories: async (includeInactive: boolean = false): Promise<CategoriesResponse> => {
    const response = await apiClient.get<CategoriesResponse>(
      API_ENDPOINTS.LEARNING.CATEGORIES,
      { params: { include_inactive: includeInactive } }
    )
    return response.data
  },

  createCategory: async (data: CreateTrainingCategoryInput): Promise<TrainingCategory> => {
    const response = await apiClient.post<TrainingCategory>(
      API_ENDPOINTS.LEARNING.CATEGORIES,
      data
    )
    return response.data
  },

  updateCategory: async (
    id: string,
    data: UpdateTrainingCategoryInput
  ): Promise<TrainingCategory> => {
    const response = await apiClient.put<TrainingCategory>(
      API_ENDPOINTS.LEARNING.CATEGORY(id),
      data
    )
    return response.data
  },

  deleteCategory: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.LEARNING.CATEGORY(id))
  },

  // ==================== Trainings ====================

  getTrainings: async (filters?: TrainingFilters): Promise<TrainingsResponse> => {
    const response = await apiClient.get<TrainingsResponse>(
      API_ENDPOINTS.LEARNING.TRAININGS,
      { params: filters }
    )
    return response.data
  },

  getTraining: async (id: string): Promise<Training> => {
    const response = await apiClient.get<Training>(
      API_ENDPOINTS.LEARNING.TRAINING(id)
    )
    return response.data
  },

  findSimilarTrainings: async (name: string): Promise<SimilarTrainingsResponse> => {
    const response = await apiClient.get<SimilarTrainingsResponse>(
      API_ENDPOINTS.LEARNING.TRAINING_SIMILAR,
      { params: { name } }
    )
    return response.data
  },

  getTrainingStats: async (id: string): Promise<TrainingStats> => {
    const response = await apiClient.get<TrainingStats>(
      API_ENDPOINTS.LEARNING.TRAINING_STATS(id)
    )
    return response.data
  },

  getTrainingReviews: async (id: string, page: number = 1, limit: number = 50): Promise<TrainingReviewsResponse> => {
    const response = await apiClient.get<TrainingReviewsResponse>(
      API_ENDPOINTS.LEARNING.TRAINING_REVIEWS(id),
      { params: { page, limit } }
    )
    return response.data
  },

  createTrainingReview: async (trainingId: string, data: CreateTrainingReviewInput): Promise<TrainingReview> => {
    const response = await apiClient.post<TrainingReview>(
      API_ENDPOINTS.LEARNING.TRAINING_REVIEWS(trainingId),
      data
    )
    return response.data
  },

  updateTrainingReview: async (trainingId: string, data: UpdateTrainingReviewInput): Promise<TrainingReview> => {
    const response = await apiClient.put<TrainingReview>(
      API_ENDPOINTS.LEARNING.TRAINING_REVIEWS(trainingId),
      data
    )
    return response.data
  },

  deleteTrainingReview: async (trainingId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      API_ENDPOINTS.LEARNING.TRAINING_REVIEWS(trainingId)
    )
    return response.data
  },

  createTraining: async (data: CreateTrainingInput): Promise<Training> => {
    const response = await apiClient.post<Training>(
      API_ENDPOINTS.LEARNING.TRAININGS,
      data
    )
    return response.data
  },

  updateTraining: async (id: string, data: UpdateTrainingInput): Promise<Training> => {
    const response = await apiClient.put<Training>(
      API_ENDPOINTS.LEARNING.TRAINING(id),
      data
    )
    return response.data
  },

  deleteTraining: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.LEARNING.TRAINING(id))
  },

  // ==================== Training Documents ====================

  // Upload a document for a training
  uploadDocument: async (trainingId: string, file: File, description?: string): Promise<{ message: string; document: TrainingDocument }> => {
    const formData = new FormData()
    formData.append('file', file)
    if (description) {
      formData.append('description', description)
    }
    const response = await apiClient.post<{ message: string; document: TrainingDocument }>(
      API_ENDPOINTS.LEARNING.TRAINING_DOCUMENTS(trainingId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  // List all documents for a training
  listDocuments: async (trainingId: string): Promise<{ data: TrainingDocument[] }> => {
    const response = await apiClient.get<{ data: TrainingDocument[] }>(
      API_ENDPOINTS.LEARNING.TRAINING_DOCUMENTS(trainingId)
    )
    return response.data
  },

  // Download document by ID
  downloadDocument: async (documentId: string): Promise<Blob> => {
    const response = await apiClient.get(
      API_ENDPOINTS.LEARNING.DOCUMENT(documentId),
      {
        responseType: 'blob',
      }
    )
    return response.data
  },

  // Delete a document
  deleteDocument: async (documentId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      API_ENDPOINTS.LEARNING.DOCUMENT(documentId)
    )
    return response.data
  },

  // ==================== Employee Training Records ====================

  getMyRecords: async (params?: {
    page?: number
    limit?: number
    year?: number
    verification_status?: string
    include_deleted_training?: boolean
    sort_by?: string
    sort_order?: string
  }): Promise<EmployeeTrainingsResponse> => {
    const response = await apiClient.get<EmployeeTrainingsResponse>(
      API_ENDPOINTS.LEARNING.MY_RECORDS,
      { params }
    )
    return response.data
  },

  getAllRecords: async (filters?: EmployeeTrainingFilters): Promise<EmployeeTrainingsResponse> => {
    const response = await apiClient.get<EmployeeTrainingsResponse>(
      API_ENDPOINTS.LEARNING.RECORDS,
      { params: filters }
    )
    return response.data
  },

  getRecord: async (id: string): Promise<EmployeeTraining> => {
    const response = await apiClient.get<EmployeeTraining>(
      API_ENDPOINTS.LEARNING.RECORD(id)
    )
    return response.data
  },

  createRecord: async (data: CreateEmployeeTrainingInput): Promise<EmployeeTraining> => {
    const response = await apiClient.post<EmployeeTraining>(
      API_ENDPOINTS.LEARNING.RECORDS,
      data
    )
    return response.data
  },

  createRecordForEmployee: async (data: AdminCreateEmployeeTrainingInput): Promise<EmployeeTraining> => {
    const response = await apiClient.post<EmployeeTraining>(
      API_ENDPOINTS.LEARNING.RECORDS_ADMIN,
      data
    )
    return response.data
  },

  updateRecord: async (
    id: string,
    data: UpdateEmployeeTrainingInput
  ): Promise<EmployeeTraining> => {
    const response = await apiClient.put<EmployeeTraining>(
      API_ENDPOINTS.LEARNING.RECORD(id),
      data
    )
    return response.data
  },

  deleteRecord: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.LEARNING.RECORD(id))
  },

  verifyRecord: async (id: string, action: 'verify' | 'reject', rejection_reason?: string): Promise<EmployeeTraining> => {
    const response = await apiClient.post<EmployeeTraining>(
      API_ENDPOINTS.LEARNING.RECORD_VERIFY(id),
      { action, rejection_reason }
    )
    return response.data
  },

  // Upload certificate (adds to list of certificates)
  uploadCertificate: async (recordId: string, file: File): Promise<{ message: string; certificate: TrainingCertificate }> => {
    const formData = new FormData()
    formData.append('file', file)
    const response = await apiClient.post<{ message: string; certificate: TrainingCertificate }>(
      API_ENDPOINTS.LEARNING.RECORD_CERTIFICATES(recordId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data
  },

  // List all certificates for a training record
  listCertificates: async (recordId: string): Promise<{ data: TrainingCertificate[] }> => {
    const response = await apiClient.get<{ data: TrainingCertificate[] }>(
      API_ENDPOINTS.LEARNING.RECORD_CERTIFICATES(recordId)
    )
    return response.data
  },

  // Download certificate by record ID (legacy - downloads first certificate)
  downloadCertificate: async (recordId: string): Promise<Blob> => {
    const response = await apiClient.get(
      API_ENDPOINTS.LEARNING.RECORD_CERTIFICATE(recordId),
      {
        responseType: 'blob',
      }
    )
    return response.data
  },

  // Download certificate by certificate ID
  downloadCertificateById: async (certificateId: string): Promise<Blob> => {
    const response = await apiClient.get(
      API_ENDPOINTS.LEARNING.CERTIFICATE(certificateId),
      {
        responseType: 'blob',
      }
    )
    return response.data
  },

  // Delete a specific certificate
  deleteCertificate: async (certificateId: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(
      API_ENDPOINTS.LEARNING.CERTIFICATE(certificateId)
    )
    return response.data
  },

  getCertificateUrl: (recordId: string): string => {
    // Return the full URL for direct viewing/downloading (legacy)
    const baseUrl = apiClient.defaults.baseURL || ''
    return `${baseUrl}${API_ENDPOINTS.LEARNING.RECORD_CERTIFICATE(recordId)}`
  },

  // ==================== Training Requirements ====================

  getRequirements: async (filters?: TrainingRequirementFilters): Promise<TrainingRequirementsResponse> => {
    const response = await apiClient.get<TrainingRequirementsResponse>(
      API_ENDPOINTS.LEARNING.REQUIREMENTS,
      { params: filters }
    )
    return response.data
  },

  createRequirement: async (data: CreateTrainingRequirementInput): Promise<TrainingRequirement> => {
    const response = await apiClient.post<TrainingRequirement>(
      API_ENDPOINTS.LEARNING.REQUIREMENTS,
      data
    )
    return response.data
  },

  bulkCreateRequirements: async (data: BulkCreateTrainingRequirementInput): Promise<{ created_count: number; message: string }> => {
    const response = await apiClient.post<{ created_count: number; message: string }>(
      API_ENDPOINTS.LEARNING.REQUIREMENTS_BULK,
      data
    )
    return response.data
  },

  updateRequirement: async (id: string, data: UpdateTrainingRequirementInput): Promise<TrainingRequirement> => {
    const response = await apiClient.put<TrainingRequirement>(
      API_ENDPOINTS.LEARNING.REQUIREMENT(id),
      data
    )
    return response.data
  },

  deleteRequirement: async (id: string): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.LEARNING.REQUIREMENT(id))
  },

  // ==================== Reports ====================

  getComprehensiveReport: async (filters?: TrainingReportFilters): Promise<{ data: ComprehensiveTrainingReport }> => {
    const response = await apiClient.get<{ data: ComprehensiveTrainingReport }>(
      API_ENDPOINTS.LEARNING.REPORT_COMPREHENSIVE,
      { params: filters }
    )
    return response.data
  },

  getReportEmployees: async (search?: string): Promise<{ data: ReportEmployee[] }> => {
    const params = search ? { search } : {}
    const response = await apiClient.get<{ data: ReportEmployee[] }>(
      API_ENDPOINTS.LEARNING.REPORT_EMPLOYEES,
      { params }
    )
    return response.data
  },

  getReportDepartments: async (): Promise<{ data: ReportDepartment[] }> => {
    const response = await apiClient.get<{ data: ReportDepartment[] }>(
      API_ENDPOINTS.LEARNING.REPORT_DEPARTMENTS
    )
    return response.data
  },

  getReportRecords: async (params?: TrainingReportFilters & {
    page?: number
    limit?: number
    sort_by?: string
    sort_order?: string
    has_certificate?: boolean
  }): Promise<{ data: TrainingRecordForReport[]; pagination: Pagination }> => {
    const response = await apiClient.get<{ data: TrainingRecordForReport[]; pagination: Pagination }>(
      API_ENDPOINTS.LEARNING.REPORT_RECORDS,
      { params }
    )
    return response.data
  },

  getCertificationStatus: async (params?: { department?: string }): Promise<{ data: CertificationStatusReport }> => {
    const response = await apiClient.get<{ data: CertificationStatusReport }>(
      API_ENDPOINTS.LEARNING.REPORT_CERTIFICATION_STATUS,
      { params }
    )
    return response.data
  },

  getEmployeesOverview: async (params?: { department?: string; search?: string }): Promise<{ data: import('../types/learning.types').EmployeeTrainingOverview[] }> => {
    const response = await apiClient.get<{ data: import('../types/learning.types').EmployeeTrainingOverview[] }>(
      API_ENDPOINTS.LEARNING.REPORT_EMPLOYEES_OVERVIEW,
      { params }
    )
    return response.data
  },
}
